// src/screens/battleView/battleViewCopy.js
//
// EVERY user-visible string the Battle View controller renders that is not
// already a Desk posture string lives here (Phase A rulings §3.4). The three
// shipped posture strings and the two Phase A additions (late, closed) stay in
// deskCopy.js so the Desk and the turn line cannot disagree (D-62); this
// module holds what is Battle-View-only: Why?, the receipts, the doors, Game
// Tape, This turn. deskHonesty.test.js scans this file and every component in
// this directory for the forbidden agent verbs — see that file's header for
// the rules and why each exists. In short:
//
//   1. SCOREBOARD LANGUAGE ONLY. Distance to a scoring tier is a fact; distance
//      to a trade is a forecast and never ships.
//   2. NO AGENT VERBS between checks. The agent acts at confirmed checks and
//      does nothing in between; nothing here implies otherwise.
//   3. RECEIPTS ARE PROVEN OR ABSENT (D-51): `Filed · Acted · Replaced ·
//      Expired`, and — since Phase B — `Heard`. Filed is not heard; heard is
//      not will-do. `Heard` is now PROVEN and therefore allowed, with its
//      claim fixed at exactly one thing (D-110): the thread was in the
//      decider's prompt at that check, proven by the cron's own stamp. It is
//      never upgraded to considered, used, noticed, understood or decided
//      because, and a withheld directive gets the reasonless system line
//      (`notHeardLabel`), never one of the four resolver words. `Holding`,
//      `Declined`, `Honored`, `Superseded` remain unproven and stay out.
//   4. THE AGENT'S OWN WORDS ONLY (C1): Why? quotes `rationale` verbatim and
//      never paraphrases it; the labels around it are scoreboard facts.
//
// Every string is a REQUEST to the design chat; change them here, never inline.
//
// A CHECK IS NAMED BY ITS SLOT (D-83). Four labels here name a check —
// `fromCheck`, `notNamedAtCheck`, `atCheck` (which `checkCardLabel` composes)
// and `nothingQueued`'s `next check ~` — and all four take `slotLabel` rather
// than `etTime`, so a tick is called the same thing wherever it appears. The
// labels that name something OTHER than a check keep the exact minute, on
// purpose: `tradeLine` / `tradeCardLine` (a swap EXECUTES at an instant),
// `filed` / `replaced` (a chat exchange), `factHeldSince` (an entry).

import { etTime } from '../../components/Dashboard/desk/deskCopy';
// Intraday Data Build 1 (contract §9.1): the ONE copy table for diagnostic
// views — zero-import, shared with the server's receipt-only replay.
import { renderIntradayDiagnosticBlock } from '../../data/intradayDiagnosticCopy';
import { slotLabel } from './deriveTurnLine';
// Phase C §1 — the door's integer comes from the ONE cap display function
// (D-122): server, client and tests share it, so `Show it · 1 of 3` cannot
// drift from what the route enforces (BUILD_RULES §9).
import { RESEARCH_CAP, researchDoorOrdinal, researchDoorEnabled } from '../../data/researchCap';
// The persisted decision record's SHARED vocabulary (voice-grounding hazard
// 26): the strings the narrator's YOUR RECORD block renders too live in the
// zero-import src/data/decisionRecord.js and are re-exposed here under their
// shipped names, so the pane and the narrator cannot disagree about one check.
import {
  WOKEN_BY_TYPE,
  wokenBy as recordWokenBy,
  NO_DECISION,
  NO_DECISION_OUTAGE,
  NO_DECISION_INCOMPLETE,
  HELD_LABEL,
  swappedLabel as recordSwappedLabel,
  DOWNGRADED_LABEL,
  FAILED_LABEL,
  MOTIVE_AGENT,
  MOTIVE_SYSTEM,
  GUARDRAIL_TYPE_WORDS,
  tierLabel as recordTierLabel,
  planAtDeployLabel,
  filesChip as recordFilesChip,
  filedLabel,
  heardLabel,
  notHeardLabel,
  evidenceHeading,
  evidenceFacts as recordEvidenceFacts,
  provenanceLine,
  regimeWord,
  NO_CHANGE_STATUS_LINE,
  FILING_CONFLICT_LINE,
  FILING_BUDGET_LINE,
  FILING_REJECTED_LINE,
  FILING_FAILED_LINE,
  RESEARCH_UNREACHABLE_LINE,
  researchFailureLine as recordResearchFailureLine,
  filingFailureLine as recordFilingFailureLine,
  GUARDRAIL_FORCED_FAILED_LABEL,
  UNCONFIRMED_LABEL,
  GUARDRAIL_FORCED_UNCONFIRMED_LABEL,
} from '../../data/decisionRecord';

/**
 * "Sep 1" — an ET calendar date, for a fact that is about a DAY (the deploy)
 * rather than an instant. deskCopy owns the time formatters both surfaces
 * share; this one is Battle-View-only, so it lives here with its one caller.
 */
const etDate = (raw) => {
  if (raw == null) return null;
  const d = raw?.toDate?.() ?? new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
};

/**
 * A tier key as the word a player reads on the tier header.
 *
 * SHARED by `atDeployTier` (A2.1b) and `tradeCardLine` (A2.2), and written as
 * a FUNCTION for a git reason as much as a style one (review L5-F1). A2.1b
 * introduced this mapping as a frozen object, A2.2 then consumed it, and
 * `git revert` of A2.1b — which D-76 requires to stay possible in isolation —
 * AUTO-MERGED that declaration away and left every trade card throwing at
 * render, with no conflict to warn anyone.
 *
 * What the rewrite bought, measured rather than assumed (review FIX-6): the
 * revert now CONFLICTS instead of corrupting. It is five conflicted files, not
 * a clean apply — `tierLabel` still falls inside a hunk git groups with the
 * A2.1b copy helpers, so a reviewer must resolve it by KEEPING `tierLabel` and
 * dropping `etDate` / `planAtDeploy` / `atDeployTier`. Resolved that way the
 * suite is green. Loud and correct beats silent and broken; it is not clean.
 */
function tierLabel(tier) {
  return recordTierLabel(tier);
}

const money = (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const BATTLE_VIEW_COPY = Object.freeze({
  // A PRICE, as this screen writes one (A2.3 review L5-F6). The row's current
  // price and the panel's `Bagger $ · Bust $` / `Entry $` are the same class of
  // number about the same piece, two lines apart — `src/utils/formatters.js`'s
  // `formatPrice` is `$${n.toFixed(2)}`, which drops the thousands separator
  // and made NVDA read `$1234.50` on the row beside `Entry $1,234.50` in the
  // panel. One formatter, so they cannot disagree (BUILD_RULES §9).
  price: (value) => money(value),

  // ── Why? (A2) ──────────────────────────────────────────────────────────────
  // The verb on a piece. Free — a pure read of persisted decision-path text.
  why: 'Why?',

  // ── Why? V2 — the piece's lines (A2.1, D-75, ruling 1) ────────────────────
  // The two SCORING tiers as prices: `thresholdBaseline × (1 ± baseATR/100)`,
  // the exact inverse of the percent the row already renders beside them and
  // the same formula the levels cron applies to V4 battles
  // (api/cron/compute-daily-baggerbomb-levels.js). Arithmetic on two persisted
  // values, never a third source — no agent battle persists a dollar level
  // (Phase 0 §2.1). NO stop line and NO alert line: the stop's entry basis is
  // fenced-private arithmetic (D-79) and `-0.5× ATR` is a wake-up trigger, not
  // a rule the agent acts on (D-78). Nothing here is estimated.
  tierPrices: (bagger, bust) => {
    const b = money(bagger);
    const s = money(bust);
    return b && s ? `Bagger ${b} · Bust ${s}` : null;
  },
  // The footer names where the two numbers come from, so the player can tell a
  // scoreboard fact from a trading level. There is no second source to name.
  fromScoringPath: 'from the scoring path',

  // The row's decision block (A2.1). The row carries the sentences that name
  // THIS piece — an extract — so its eyebrow says where they came from; the
  // book panel keeps `atCheck` below, because it IS the whole check.
  fromCheck: (iso) => {
    const t = slotLabel(iso);
    return t ? `From the ${t} check` : null;
  },
  // The door out of the extract and into the whole check — which, since D-89,
  // is the check's own CARD in the conversation, not the panel at the top of
  // the board. The words stay the same because what the player asked for did
  // not: they want the whole check, and the tape is where it lives whole,
  // beside the checks either side of it. The panel above the board carries the
  // book's latest check and opens collapsed; it was never the place a row's
  // reader was heading.
  readFullCheck: 'Read the full check',
  // A truthful state, not an absence of data: the check recorded words, and
  // none of its sentences named this piece. The full paragraph is one tap away.
  notNamedAtCheck: (iso) => {
    const t = slotLabel(iso);
    return t ? `Not named at the ${t} check` : null;
  },

  // Why the tick ran at all (A2.1, D-78). `evaluation.triggers` persists the
  // TYPES only (agent-evaluate.js:2651 `triggers.map(t => t.type)`); the detail
  // string is not persisted, so the copy names the type and nothing more. A
  // trigger wakes the decider — it is not a rule the agent acts on, and this
  // line never becomes an "alert level".
  //
  // ALL NINE persisted types are now ruled (D-81; the addendum's ruling 2
  // accepted the A2 handover's §5 proposals with one edit — `near`, not
  // `nearing`, because a piece's distance to a tier is a STATE and `nearing`
  // is a motion the surface cannot see). Each names a FACT ABOUT WHY THE
  // MODEL WAS WOKEN and never what it will do: a trigger is the gate's reason
  // for running the tick, not a rule the agent acts on (D-78).
  //
  // An UNKNOWN type still renders NOTHING — never a raw type string. The gate
  // is the only writer, and a tenth type added there arrives here unruled and
  // silent until it has its own sentence.
  wokenByType: WOKEN_BY_TYPE,
  wokenBy: (triggers) => recordWokenBy(triggers),

  // ── The guardrail's provenance code, in plain words (A2.3, D-80, ruling 1) ─
  // The cron composes a forced exit's rationale as
  // `Guardrail override (${result.sourceNote || 'hard'}): ${overrideNote}`
  // (api/cron/agent-evaluate.js:2124), so the guardrail module's own
  // `guardrail_${forcedType}` token (api/_utils/agentGuardrails.js:495 / :558)
  // rides INSIDE the sentence C1 renders verbatim. The table that translates
  // it, the ruling that separates the two rules, and the reason the three
  // words are the founder's existing swap-ledger taxonomy all moved to
  // src/data/decisionRecord.js beside `renderMotive` (voice-grounding hazard
  // 26: the narrator's YOUR RECORD block renders the same sentence on the
  // server). Re-exposed here under its shipped name — `COPY.guardrailTypeWords`
  // and its source tripwire in TapeCards.render.test.jsx are unchanged.
  guardrailTypeWords: GUARDRAIL_TYPE_WORDS,

  // ── The plan at deploy (A2.1b, D-76) ──────────────────────────────────────
  // The deploy decision's own persisted output, frozen at creation and in
  // front of the decider on every tick. It is HISTORY: every label carries the
  // deploy date so it can never read as a decision made now. Gated by
  // selectDeployPlan.js — a tournament battle's plan and the algorithmic
  // fallback's template are SYSTEM strings and never render (C1).
  // Null — the caller omits the section — when there is no deploy date to
  // stamp (review L5-F6). D-76 requires the date precisely so the block reads
  // as history; an undated `The plan at deploy` is a string the ruling does
  // not contain, and the honest branch is the one `tierPrices` already takes
  // when its inputs are missing: render nothing rather than something weaker.
  planAtDeploy: (iso) => planAtDeployLabel(etDate(iso)),
  // A row shows only its TIER's rationale, and only the sentences of it that
  // name the row's piece. The label says "tier" out loud, because the sentence
  // was written about the tier — never about this position alone.
  atDeployTier: (tier) => {
    const label = tierLabel(tier);
    return label ? `At deploy · ${label} tier` : null;
  },

  // The panel header names the CHECK (the tick), never the piece: the tick's
  // rationale is the agent's reasoning for the book at that check, and the
  // header must not imply a per-position record that does not exist.
  atCheck: (iso) => {
    const t = slotLabel(iso);
    return t ? `At the ${t} check` : null;
  },

  // The three decision states, in the order the panel branches: downgraded
  // FIRST (hazard 2 — a swap the model argued for that a guardrail held still
  // carries a swap rationale), then the decision.
  downgradedLabel: DOWNGRADED_LABEL,
  downgradedFooter: 'The agent\'s own words · the system held it',

  // The FOURTH state (A4.0, D-66). `downgraded === true` is also stamped when
  // executeSwapServer threw (agent-evaluate.js: `validationErrors[0]` =
  // `Swap execution failed: …`) — no guardrail held anything; the swap the
  // agent argued for simply did not go through. selectWhyState.js branches
  // on that prefix, so the guardrail label never over-claims on this path.
  failedLabel: FAILED_LABEL,
  failedFooter: 'The agent\'s own words · the position stayed as it was',

  // The FIFTH state (A2.0, D-70). A guardrail — not the agent — called for the
  // swap: applyGuardrails returned SWAP with `sourceNote: guardrail_*` and a
  // `forced_exit` override, the cron OVERWROTE the rationale with its own
  // `Guardrail override (…): …` text, and the swap was then blocked (a
  // distressed replacement, validation) or threw. Under the fourth state's
  // words that tick read `Argued for a swap · The agent's own words` over text
  // the agent never wrote (A4 handover item 21). The subject is the guardrail
  // and the footer names whose reason follows.
  guardrailForcedFailedLabel: GUARDRAIL_FORCED_FAILED_LABEL,
  guardrailForcedFailedFooter: 'The guardrail\'s reason · the position stayed as it was',

  // The UNCONFIRMED states (table G, V1.4 — founder decision Q3, option (a)).
  // The entry carries `executionOutcome: 'unknown'`: the executor threw and
  // the server's own read-back failed, so whether the swap landed is not
  // known. The label says exactly that, with the same subject the fourth and
  // fifth states use (the agent, or the guardrail under the D-70 gate). There
  // is no outcome footer — `the system held it` and `the position stayed as it
  // was` are both unproven here — only the author line (`motiveAgent` /
  // `motiveSystem`, by the one motive-author rule).
  unconfirmedLabel: UNCONFIRMED_LABEL,
  guardrailForcedUnconfirmedLabel: GUARDRAIL_FORCED_UNCONFIRMED_LABEL,
  heldLabel: HELD_LABEL,
  swappedLabel: (symbolOut, symbolIn) => recordSwappedLabel(symbolOut, symbolIn),

  // The absence state is a real state, not a failure (honesty rule 7): the
  // tick ran (lastScoredAt advanced) and recorded no evaluation entry.
  noDecision: NO_DECISION,
  // The more specific absence (A4.0, D-65): the latest entry carries
  // `haikuError` with `failureClass: 'timeout'` — the model call timed out
  // and the tick defaulted to HOLD with the system's placeholder words. The
  // fact is persisted on the entry itself; the label states it and nothing
  // more. Every other outage class takes the class-neutral line below (D-69).
  noDecisionOutage: NO_DECISION_OUTAGE,
  // Every OTHER outage class (A2.0, D-69): `budget_skipped`, `truncated_response`,
  // an HTTP status, an error's class name, `unknown`. The tick recorded an entry
  // whose words are the cron's placeholder, so a decision is still absent — but
  // "timed out" would name a verb the evidence does not support (honesty rule 8,
  // A4 review L1-F1). `did not complete` is true of every class including the
  // timeout; the timeout keeps the more specific line above.
  noDecisionIncomplete: NO_DECISION_INCOMPLETE,

  // Trades on this piece today — the section heading; each line is the swap
  // receipt's own time, symbols and reason (engine text, verbatim).
  thisPieceToday: 'This piece today',
  tradeLine: (iso, symbolOut, symbolIn) => {
    const t = etTime(iso);
    const pair = `${symbolOut ?? '—'} → ${symbolIn ?? '—'}`;
    return t ? `${t} · ${pair}` : pair;
  },

  // Facts — scoring facts about the piece. NO lock line: the row has no lock
  // tag and none is computed (hazards 6, 16). Distance to the next tier is the
  // row's own rendered proximity text, passed in, never re-derived.
  factEntry: (price) => {
    const m = money(price);
    return m ? `Entry ${m}` : null;
  },
  factHeldSince: (iso) => {
    const t = etTime(iso);
    return t ? `Held since ${t}` : null;
  },

  // The one door (D-45, D-53): the follow-up field, prefilled with a string
  // the user edits and sends through the shipped chat path. Costs a message.
  askFollowUp: 'Ask a follow-up · 1 message',
  followUpPrefill: (symbol) => `About ${symbol} — `,

  // ── The piece scope (A2.3, D-73) ──────────────────────────────────────────
  // The SECOND door: how much of the conversation is about this piece, and the
  // way into it. `n` is the LENGTH of the list the tap opens (scopeTape.js) —
  // never a second count — so the number and the filter cannot drift as rules
  // (BUILD_RULES §9); see that module's header for the one input they do not
  // share. Zero is a real answer and still renders: `In the chat · 0` says
  // something true about the piece, and the tap opens the WHOLE tape at its
  // composer prefill rather than a filter onto nothing (seed §A2.3). Free — a
  // count of what is already on screen.
  inTheChat: (n) => (Number.isFinite(n) ? `In the chat · ${n}` : null),
  // The chip on the scoped stream. `All` is the way OUT — the whole tape —
  // and it is a noun about the display, never a claim about the battle.
  scopeChip: (symbol) => (symbol ? `${symbol} · All` : null),
  // The two ACCESSIBLE names for the scope's ends (review RB-F10). Neither
  // visible label says what its button does: `In the chat · 2` reads as a
  // count and `NVDA · All` as a label, so a screen reader announced a number
  // and a word where a filter was about to be applied or cleared. Named for
  // the ACTION, exactly as `whyName` and `sheetHandleName` are.
  scopeDoorName: (symbol, n) => (Number.isFinite(n)
    ? `Show the ${symbol} messages · ${n} in the chat`
    : null),
  scopeChipName: (symbol) => (symbol ? `Showing ${symbol} only · show the whole tape` : null),

  // ── Show it (Phase C §1, D-116 / D-122) ───────────────────────────────────
  //
  // THE SECOND VERB. Tap a name and the platform shows you what it knows. Two
  // surfaces wear the words and they are deliberately different:
  //
  //   · the CHIP the character offers in the conversation names the NAME
  //     (`Show it · MPC`) — the chip's whole job is to say which piece;
  //   · the DOOR on a piece's own panel names the COST (`Show it · 1 of 3`),
  //     the D-31 cost-before-the-tap idiom `Ask a follow-up · 1 message` uses,
  //     because the panel is already about that piece and the scarce thing is
  //     the read.
  //
  // The door's integer is the ORDINAL OF THE NEXT CARD from the one shared
  // display function (src/data/researchCap.js, D-122) — never a second count
  // (BUILD_RULES §9). The exhausted door reads `3 of 3` exactly as the last
  // enabled one does and is distinguished by being disabled, so the text is
  // read off `used` alone and the enabled state off `researchDoorEnabled`.
  showItChip: (symbol) => (typeof symbol === 'string' && symbol.trim() ? `Show it · ${symbol.trim()}` : null),
  showItDoor: (used) => `Show it · ${researchDoorOrdinal(used)} of ${RESEARCH_CAP}`,
  // The ACCESSIBLE name (the scopeDoorName rule): the visible label reads as a
  // ratio, so a screen reader would announce two numbers where a read is about
  // to be spent. Named for the ACTION and for the name it acts on.
  // The exhausted state rides the NAME, not a `title` (review F-8). `aria-label`
  // wins the accessible-name computation, so a `title` beside it is a
  // description many AT pairings never announce; a `disabled` button is out of
  // the tab order, so a keyboard user cannot focus it to surface a tooltip; and
  // `title` never appears on touch at all. Without this, those users learn the
  // door is spent only from `opacity: 0.5` and a tap that does nothing — which
  // is indistinguishable from a broken button.
  showItDoorName: (symbol, used) => {
    const s = typeof symbol === 'string' ? symbol.trim() : '';
    if (!s) return null;
    const base = `Show the platform's data on ${s} · read ${researchDoorOrdinal(used)} of ${RESEARCH_CAP}`;
    return researchDoorEnabled(used) ? base : `${base} — ${BATTLE_VIEW_COPY.showItExhausted}`;
  },
  // The exhausted door's own line — the only place the three-of-three state
  // says anything the enabled door does not. It states the fact, never a
  // remedy the platform does not have.
  showItExhausted: 'All 3 reads used in this battle.',
  // The one failure line the client can be held to (the filingFailureLine
  // rule): it says the read did not happen and nothing about why the platform
  // thinks that. THE SENTENCE IS THE DOOR'S TOO, declared once
  // (decisionRecord.js `RESEARCH_UNREACHABLE_LINE`) — `Try again.` is the
  // CHAT's own clause, because the chip's error sits in the composer's slot
  // where the retry has to be named. Byte-identical to the shipped string.
  showItFailed: `${RESEARCH_UNREACHABLE_LINE} Try again.`,
  // THE DOOR'S OWN failure, and WHICH sentence it is depends on what the
  // failure proves. The door is a different surface from the chip above: it IS
  // the retry — right there, still enabled, still reading `Show it · 2 of 3` —
  // so what the player needs from it is not an instruction but the state of
  // the count that door just named.
  //
  // A refusal the route ATTESTED (its body carried `noCardWritten`, which only
  // its pre-transaction paths do) proves no card was written and therefore no
  // slot consumed, and the line says so. Anything else — an unattested
  // refusal, a platform 504, a request that never came back — proves only that
  // no read came back, and the line says only that. The D-90 split, made by
  // one function in decisionRecord.js so the two doors cannot answer
  // differently. (The chat's chip is not on it yet — see that docstring.)
  showItDoorFailed: (attested) => recordResearchFailureLine(attested),
  // D-54's forward path, as a word on the card. `Equip` is the shipped verb for
  // putting a name in front of the agent; the card offers it and never promises
  // what the process will do with it.
  showItEquip: 'Equip',
  // What the scoped stream announces when the filter lands or lifts. A live
  // region, so it is spoken without moving focus — which the door does not
  // move to the stream anyway (it goes to the composer, with the prefill).
  scopeAnnounce: (symbol, n) => (symbol
    ? `Showing ${n} ${symbol} ${n === 1 ? 'entry' : 'entries'}`
    : 'Showing the whole tape'),

  // ── What KIND of entry this is (flip-prep, extends D-84) ──────────────────
  //
  // D-84 made the tape's four visual CLASSES unmistakable — speech, the
  // player's messages, engine records, directive cards. This names the kinds
  // INSIDE the speech class, which the eye cannot separate: a bench note, a
  // trade narration, the seeded opener and an answer to something the player
  // typed all arrive as the same left bubble in the same voice, and only the
  // record says which is which.
  //
  // FROM THE PERSISTED TYPE, NEVER FROM THE TEXT. Every value below is one the
  // server writes on the exchange itself — `first_message` (decide.js:1639,
  // ensure-opener.js:114), `anticipation` (voiceLayerAnticipation.js:200),
  // `trade_narration` (voiceLayerTradeNarration.js:203) — so an eyebrow is a
  // fact about the record rather than a reading of the prose. Guessing from
  // the words is exactly the class of inference hazard 24 forbids elsewhere on
  // this screen, and it would be wrong the moment a character mentions the
  // bench in an ordinary reply.
  //
  // AN UNKNOWN TYPE GETS NO EYEBROW. A new server type must reach the design
  // chat and get a word before it reaches the screen; falling back to a
  // neighbour's label would put a name on something nobody has named.
  //
  // `auto_debrief` is deliberately absent: it already has the shipped
  // `Post-Market Debrief` eyebrow (RENDER_CONFIG), and one exchange with two
  // eyebrows is worse than one with none.
  tapeKindEyebrow: (messageType, hasUserHalf, anticipationDirection = null, grounded = false) => {
    if (messageType === 'first_message') return 'Opener';
    // `Bench note` IS the bench's word, and the record splits the kind in two
    // (review L1-F1). `anticipationCandidates[].direction` is a required enum:
    // `potential_entry` is a bench candidate worth bringing in — a bench note,
    // exactly as ruled — while `potential_exit` is an ACTIVE HOLDING whose
    // signal profile degraded enough that leaving is plausible. That second
    // one is a note about a piece in the player's OWN BOOK, and calling it a
    // bench note is the reading-the-prose error this map exists to avoid, one
    // level down: the record disambiguates and the label ignored it.
    //
    // Only the ruled case gets the ruled word. The other gets NOTHING, by the
    // same rule an unknown type does — a word for it has to be ruled before it
    // reaches the screen, and inventing one here would be the guess. Recorded
    // for the founder; a direction-aware pair is one line when there is a
    // second word to use.
    //
    // THE SECOND WORD IS NOW RULED (voice-layer grounding spec §5, the Sep 7
    // founder adoption): a `potential_exit` note is about a piece in the
    // player's OWN BOOK, and its word is `Holding note` — for a note produced
    // UNDER THE GROUNDING CONTRACT (`grounded`: the exchange carries the
    // marker). A legacy, model-written exit note keeps no eyebrow, exactly as
    // shipped, so the flag-off page is byte-identical and the word is never
    // put on a forecast the retired prompt wrote (review R-02). A record with
    // no direction still gets nothing.
    if (messageType === 'anticipation') {
      if (anticipationDirection === 'potential_entry') return 'Bench note';
      if (anticipationDirection === 'potential_exit') return grounded ? 'Holding note' : null;
      return null;
    }
    if (messageType === 'trade_narration') return 'Trade note';
    // `research` is DELIBERATELY ABSENT from this map, exactly as `auto_debrief`
    // is: the research card carries its OWN `Research` eyebrow, composed onto it
    // by the server (decisionRecord.js RESEARCH_EYEBROW), and one exchange with
    // two eyebrows is worse than one with none — a second one here would also be
    // a second source for the same word (BUILD_RULES §9). An explicit `return
    // null` for it was REMOVED (review F-7): the fall-through already returns
    // null for every unnamed type, so the line could not fail under its own
    // deletion, and §2 says a branch that cannot fail is not a guard. The rule
    // lives here, in the same place `auto_debrief`'s does.
    // `Reply` is a claim about a PAIR — the player wrote and the character
    // answered — so it needs the user half to exist. `deriveChatMessages`
    // defaults a legacy exchange with no type to `user_initiated`, and one of
    // those with no `userMessage` is an agent-initiated exchange nobody
    // labelled: calling its answer a reply would invent the question.
    if (messageType === 'user_initiated' && hasUserHalf) return 'Reply';
    return null;
  },

  // ── The tape (A2.2, D-72 / D-77) ──────────────────────────────────────────
  // A trade card per EXECUTED swap: when, the pair, the tier. The tier is the
  // scoring tier the closed position sat in, from the trade record itself.
  tradeCardLine: (iso, symbolOut, symbolIn, tier) => {
    const t = etTime(iso);
    const pair = `${symbolOut ?? '—'} → ${symbolIn ?? '—'}`;
    return [t, pair, tierLabel(tier)].filter(Boolean).join(' · ');
  },
  // The points the closed position locked in — a scoreboard fact, from the
  // trade record's own `lockedPoints`. Absent when the record carries none.
  banked: (points) => {
    if (typeof points !== 'number' || !Number.isFinite(points)) return null;
    return `Banked ${points.toFixed(1)} pts`;
  },
  // WHOSE WORDS the motive is (ruling 5). The engine writes the rationale on
  // the risk loop, the guardrail path and the R11 pass; only the model's own
  // swap carries the agent's argument. An unlabelled system sentence under
  // the agent's name is the C1 failure this pair exists to prevent.
  motiveAgent: MOTIVE_AGENT,
  motiveSystem: MOTIVE_SYSTEM,
  // The shipped echo on a swap the model attributed to a directive (D-51's
  // `Acted`, unchanged wording).
  fromDirective: '↳ from directive',

  // A check card per decided check: what KIND of entry it is, the tick's own
  // slot, and its state label, in one line — then the first sentence of the
  // rationale. `Status check` is the check card's answer to the same question
  // `Bench note` and `Opener` answer for the speech kinds: a stream of records
  // and bubbles should say what each thing IS without the player parsing it.
  //
  // TWO DEFENSIVE BRANCHES CAME OFF (review L4-F9). `!t` and `!label` were both
  // unreachable from the only caller: `buildCheckEntries` emits an entry only
  // when the instant is readable, and `selectWhyState` sets a label on every
  // branch it can return. Three mutations walked the suite through them, which
  // is what an unreachable branch always does — §2's rule is that a branch
  // which cannot fail is not a guard, and an unreachable one cannot even be
  // reached to fail. The remaining `label &&` IS reachable and is the
  // absence-label rule.
  checkCardLabel: (iso, label) => {
    const t = slotLabel(iso);
    // THE TWO ABSENCE LABELS ALREADY END IN "at this check" (D-65, D-69), so
    // the kind word in front of them says "check" twice in one line (the
    // stutter review L5-F7 found in the previous composition, in its new
    // shape). The slot alone carries the same fact, and the ruled string is
    // untouched — those two are the founder's words and rewording them to fit
    // an eyebrow would be a ruling, not a composition.
    if (label.includes('at this check')) return `${t} · ${label}`;
    return `Status check · ${t} · ${label}`;
  },
  readMore: 'Read more',
  // D-89 — the book panel's own close. The glyph is decorative; this is the
  // accessible name, and it says what the control does rather than naming the
  // shape it is drawn as. Focus returns to the score header that opened it,
  // which is the disclosure contract the header's `aria-expanded` promises.
  closeWhyBookName: 'Close the check',
  // A run of consecutive checks that changed nothing a player can see (D-77):
  // HOLD, not downgraded, no outage, banked score unchanged, the position set
  // unchanged and the directive disposition unchanged. The live TOTAL is
  // deliberately not among them — it moves with price on nearly every tick,
  // and the board already shows it.
  checksNoChange: (n) => `${n} checks · no change`,
  // A check the evaluation loop never reached: the run's time budget ran out
  // before this battle's turn, so nothing was decided and no agent verb
  // applies — the line names the LOOP, never the agent (rule 2). Rendered off
  // the server's `check_deferred` status-feed beat (EVAL_DEFERRED_BEAT_ENABLED;
  // docs/audits/20260923_BUILD_EVAL_DEFERRED_BEAT.md). Founder copy, verbatim.
  checkDeferredEyebrow: 'Check deferred',
  checkDeferredLine: 'The loop ran out of time before reaching this battle · next run picks it up first',

  // ── This turn (A3) ─────────────────────────────────────────────────────────
  // Strict membership (D-49): only the current directive. `Filed {t}` is the
  // filing EXCHANGE's timestamp. No "for the ~{t} check" — filed is not heard
  // (hazard 3). Empty is a truthful state, stamped with the adapter's next.
  thisTurn: 'This turn',
  filed: (iso) => filedLabel(etTime(iso)),
  nothingQueued: (nextIso) => {
    const next = slotLabel(nextIso);
    return next ? `Nothing queued · next check ~${next}` : 'Nothing queued';
  },

  // ── Receipts (A3, D-51 / D-60) ─────────────────────────────────────────────
  // `Filed · Acted · Replaced · Expired`. Acted is the shipped `↳ from
  // directive` on a statusFeed swap entry (AgentChat.jsx) and stays there.
  // Replaced shows text and time only — never "never seen". Expired is the
  // bare word: it is a battle-complete fact, not a time.
  // The directive card's eyebrow under the controller flag (A4.0, D-68): the
  // receipt line carries the state, so the eyebrow names the thing, not a
  // state (`DIRECTIVE LOCKED IN` above `Replaced {t}` contradicted itself).
  // Flag-off keeps the shipped label byte-for-byte until bug 2's own PR.
  directiveEyebrow: 'Directive',
  replaced: (iso) => {
    const t = etTime(iso);
    return t ? `Replaced ${t}` : 'Replaced';
  },
  expired: 'Expired',

  // ── Heard (Phase B, seed §1; D-110) ───────────────────────────────────────
  // The receipt's second line. `Heard at the {slot} check` when the record
  // proves the thread was in the decider's prompt at that check; the
  // reasonless `Not heard at the {slot} check` when a directive existed and
  // the assembler withheld it (Sol M-1 — the four suppression words are
  // telemetry, never copy); NOTHING when no entry names the thread at all,
  // which is the honest answer for a filing that landed mid-tick and will be
  // stamped on the NEXT check.
  //
  // BOTH take the same slot from the same stamp. The negative was deictic
  // (`Not heard at this check`) and is not any more, which is what lets the
  // two verdicts share one rule below instead of one each.
  //
  // The slot goes through `slotLabel` like every other label that names a
  // check (D-83) — the same formatter the tape and the turn line use, so one
  // tick is called one thing everywhere (BUILD_RULES §9).
  heard: (iso) => heardLabel(slotLabel(iso)),
  notHeard: (iso) => notHeardLabel(slotLabel(iso)),

  /**
   * The second line for a receipt carrying a Heard stamp, or null.
   * Takes the whole receipt so the card and the strip cannot disagree about
   * which of the three outcomes they are in.
   */
  // ── What the check saw (Phase B, seed §2; D-111) ──────────────────────────
  // The evidence the decider's prompt rendered for this held piece, under a
  // heading that names the CHECK by its slot (D-83). The eight labels, the
  // `chg` carve-out and the risk carve-out all live in decisionRecord.js so
  // the narrator's record renders one check with one vocabulary (hazard 26).
  //
  // The provenance line is DETAIL, not a freshness promise: `techAt` is the
  // newest held technical document's stamp, `fundAsOf` the FUNDAMENTALS
  // block's own header date. Both name themselves; neither claims the eight
  // values are current as of anything (Sol M-2). These are DOCUMENT INSTANTS,
  // not checks, so they keep their exact minute like `tradeLine` and `filed`
  // — only a CHECK is named by its slot.
  evidenceHeading: (iso) => evidenceHeading(slotLabel(iso)),
  // The panel's facts, as ENTRIES rather than strings: `text` is the line the
  // decider's prompt printed, `label` the line a player reads, `title` the raw
  // token a translated label stands for. One walk feeds this and the
  // narrator's `evidenceFactLines` (decisionRecord.js), so the panel's regime
  // word and the prompt's regime token are the same token by construction.
  evidenceFacts: (evidence) => recordEvidenceFacts(evidence),
  // The check's own instant goes with it, so a vintage from another ET day
  // carries its date rather than reading as a time later today (review A-3).
  evidenceProvenance: (vintages, checkIso = null) => provenanceLine(vintages, etTime, checkIso),
  // Intraday Data Build 1 (§9.1): the diagnostic block for one piece from a
  // stored view — header first, then the lines from the fixed copy table,
  // instants through the same ET formatter as every other check line. Empty
  // when the view carries nothing for the piece.
  intradayDiagnostic: (view, symbol) => renderIntradayDiagnosticBlock(view?.symbols?.[symbol] ?? null, { timeText: (ms) => etTime(new Date(ms).toISOString()) }),
  regimeWord: (value) => regimeWord(value),

  // BOTH VERDICTS TRAVEL, BECAUSE BOTH NOW NAME THEIR OWN CHECK.
  //
  // A stamped sentence is a past fact about ONE named check: the thread was in
  // the decider's prompt at the 12:45 check, or it was not. Either way the
  // sentence is true wherever it is read — and the scrollback card is exactly
  // where that past fact is worth keeping, because the receipt above it says
  // only what became of the directive, never what the decider was handed while
  // it stood. So the line renders on ANY directive card whose thread carries a
  // stamp: Filed, Replaced and Expired alike.
  //
  // The asymmetry that stood here was a fact about the SENTENCES, not about
  // the verdicts. `Not heard at this check` was DEICTIC — it named no slot, so
  // `this check` could only mean the LATEST one, which on a Replaced or
  // Expired card is a check the record says nothing about — and it was scoped
  // to the `filed` receipt for that reason. `notHeardLabel` names the slot, so
  // the reason is gone and the scoping went with it (decisionRecord.js). One
  // sentence shape, one rule, one word different.
  //
  // The state gate that remains is the STRIP's, below, and it is a different
  // rule for a different reason.
  heardLine: (receipt) => {
    const stamp = receipt?.heard;
    if (!stamp || typeof stamp !== 'object') return null;
    if (stamp.heard === true) return BATTLE_VIEW_COPY.heard(stamp.at);
    if (stamp.heard === false) return BATTLE_VIEW_COPY.notHeard(stamp.at);
    return null;
  },

  /**
   * The *This turn* strip's Heard line — THE CURRENT CARD'S TREATMENT, ALWAYS.
   *
   * The widening above is a rule about DIRECTIVE CARDS, where a displaced
   * thread's card is scrollback and a past fact about it is worth keeping. The
   * strip is not a card: it holds only what is unresolved and check-bound, and
   * presence in it is itself a claim that something is outstanding for the
   * next check. A past-tense line about a thread that is no longer the
   * directive — `Heard at the {slot} check` or `Not heard at the {slot} check`
   * — would contradict that contract, so the strip keeps the `filed` gate the
   * cards no longer need.
   *
   * `deriveReceipts` never hands the strip a non-`filed` receipt today — the
   * slot's thread is `currentId`, and the strip returns null on `completed` —
   * so this is a contract made structural rather than a live fix (review C-1).
   * It lives here, beside the card's rule, so each surface still has exactly
   * one place that decides (BUILD_RULES §9).
   */
  thisTurnHeardLine: (receipt) => (
    receipt?.state === 'filed' ? BATTLE_VIEW_COPY.heardLine(receipt) : null
  ),

  // ── The chat's send failure (A2.3, addendum item 11) ──────────────────────
  // The shipped line is `Agent is thinking too hard. Try again.` — an agent
  // verb (honesty rule 2) on a sentence that is not even about the agent: the
  // model was never reached, so nothing it did or did not do explains the
  // failure. It also leaves the player unsure whether the message went, and
  // the founder's smoke found exactly that doubt: three failed sends, the
  // budget still reading 0/10.
  //
  // The line first said two things — the character could not answer, and
  // `· nothing was sent`. THE SECOND CLAUSE IS GONE (flip-prep item 5), and it
  // is worth saying why, because it was the half the ruling cared about.
  //
  // The justification was that the budget is prop-driven from the server's own
  // write and a failed request produces no write. That is false. In
  // `api/agent/chat.js` the durable write and the budget increment are ONE
  // `battleRef.update()` — `chatExchanges: arrayUnion(exchange)` beside
  // `[budgetField]: increment(1)` — INSIDE the `try` whose `catch` returns the
  // 500, so anything throwing after it returns 500 with the exchange appended
  // and the player charged. The same file flags exactly this asymmetry about
  // its League charge a few lines below and not about this one. Reproduced on
  // the real send path: `1/10` and "nothing was sent" on screen together
  // (A2 review RB-F4). The client's `catch` branch makes the same promise on
  // any network drop, which cannot prove the server did nothing either.
  //
  // Item 11's own reasoning is what condemns the clause: "a message the player
  // believes was spent is a message they will not send again". The mirror is
  // worse — a message that WAS spent, believed free, re-sent, charged twice
  // out of ten — and it is the one this sentence was causing.
  //
  // So the line says only what the client can see. `nothing was sent` comes
  // back when the server attests to it, and that attestation rides the P-1
  // concurrency branch. Flag-off keeps the shipped string until bug 2's own PR.
  chatSendFailed: 'The character couldn\'t answer just now',

  // ── The arena header (A3.0, D-96) ──────────────────────────────────────────
  // The score header becomes the arena: the player's side tinted --ft-teal, the
  // CPU's --ft-copper, the tug-of-war bar as the seam between them.
  //
  // `VS` sits in the CENTRE slot so the accessible reading order is player →
  // VS → CPU, which is the order the eye takes and the order the scores mean.
  // Upper case, against the arena mock's lower-case `vs`: this is a scoreboard,
  // and the header's own name row is already upper case.
  //
  // `Tap for the book` is a NEW visible string. The book tap surface has shipped
  // since Phase A with an aria-label and nothing a sighted player could read —
  // the whole book's Why? was discoverable only by trying the header. Desktop
  // only: on a phone the header is tighter and the turn line has the row to
  // itself.
  arenaVs: 'VS',
  arenaBookHint: 'Tap for the book',

  // ── The character (A3.1, D-91 / D-98) ──────────────────────────────────────
  // The avatar is the ONE door to the conversation, so its accessible name says
  // what it opens, not what it is drawn as. The unread count rides that name
  // rather than living only in a badge — a badge is a shape, and a shape is not
  // a name.
  //
  // `{n} new` is the badge's own label (the seed's string). It never counts raw
  // feed actions: the number is the difference between what the TAPE renders and
  // what the reader has seen (D-88).
  paneName: 'The agent\'s pane',
  paneOpen: 'Open the agent\'s pane',
  paneUnread: (n) => `${n} new`,
  paneOpenName: (n) => (n > 0
    ? `${BATTLE_VIEW_COPY.paneOpen} · ${BATTLE_VIEW_COPY.paneUnread(n)}`
    : BATTLE_VIEW_COPY.paneOpen),
  // The bubble is a second door onto the same pane. Its name leads with the
  // kind it is showing, so a screen reader hears WHAT landed before it hears
  // what the control does; a bubble with no kind word (a folded run) names the
  // action alone rather than an empty prefix.
  paneBubbleName: (eyebrow) => (eyebrow
    ? `${eyebrow} · ${BATTLE_VIEW_COPY.paneOpen.toLowerCase()}`
    : BATTLE_VIEW_COPY.paneOpen),

  // ── The pane (A3.2, D-91 / D-93) ───────────────────────────────────────────
  // Three sections, three words. They are the tabs' visible labels AND their
  // accessible names — a segmented control is a real tablist here, so the word
  // the eye reads is the word a screen reader announces.
  paneSectionChat: 'Chat',
  paneSectionBench: 'Bench',
  paneSectionTape: 'Tape',
  // The way out, named for what it does on the shell it appears on. Desktop
  // COLLAPSES (the board takes the full width, the pane is still there); mobile
  // CLOSES (the pane was covering the board). Two words because they are two
  // different promises — the A2 containers' longer strings (`Collapse the
  // chat`, `Open the chat`) named a chat; these name a place.
  paneCollapse: 'Collapse',
  paneClose: 'Close',
  // The overflow's control (A3.5 fills it). Named for what it holds, not for
  // the three dots it is drawn as.
  paneMore: 'More',
  // A3.5 (D-95): the overflow holds `Report a bug` ALONE. The mock's title
  // offers `Read · Equip · Report a bug`; Read and Equip are not built. The
  // string is the widget's own aria-label (ClashBotWidget.jsx), taken from copy
  // here rather than left as a literal in two places.
  paneReportBug: 'Report a bug',

  // ── Bench (A3.3, D-92) ─────────────────────────────────────────────────────
  // `Not named at the {t} check` (notNamedAtCheck) already exists and is REUSED
  // — Bench is not allowed a second way of naming a check (D-83), and the
  // heading below takes its slot from the same `slotLabel`.
  //
  // The absence line is a truthful state, not an error: no entry today carries
  // words at all. It is deliberately NOT "the agent has not checked yet" —
  // ticks may well have run; what is absent is WORDS.
  benchNoCheck: 'No check yet today',
  // The heading over the names the decider named — the SLOT OF THE CHECK
  // ACTUALLY USED, not "the last check" (founder ruling Sep 4, on the review's
  // open question 2). Under the scan-back the words may come from a check that
  // is not the last one: the turn line can say `Checked 1:00 PM` while these
  // sentences are the 12:45 check's, and a heading that said "the last check"
  // was then simply false.
  //
  // ONE string, not two. The first draft paired `Named at the last check` with
  // a separate `At the {t} check` line beneath it, which said "check" twice in
  // two lines — the stutter review L5-F7 found in its own shape. The slot lives
  // in the heading now and the second line is gone.
  benchNamed: (iso) => {
    const t = slotLabel(iso);
    return t ? `Named at the ${t} check` : null;
  },
  // The section's subtitle: the equipped watchlist's BARE name. The header's
  // chip prefixes it with `Watchlist: ` (watchlistEquipUI.getEquippedWatchlistLabel);
  // here the section heading already says Bench, so the prefix would stutter.
  benchWatchlist: (name) => (name ? `${name} · equipped` : null),
  // The rest of the roster — the names this check did not mention.
  benchRest: 'The rest of the roster',
  // Phase B (seed §3, D-112): THE FACT OF THE FLAG, and nothing else. The
  // decider's own anticipation output named this bench name as a potential
  // entry at this check. Not "the agent is eyeing it", not "about to buy it",
  // not why — `signalSummary` and `threshold` are persisted and never render
  // here (D-103). One word, and it is a past fact about a check that has
  // already run, so it carries no forecast (the C1 rule the whole surface is
  // built on: distance to a scoring tier is a fact, distance to a TRADE is a
  // forecast and never ships).
  benchFlaggedChip: 'Flagged',

  // ── Tape (A3.4, D-94) ──────────────────────────────────────────────────────
  // The shipped Game Tape's content, moved into the pane and simplified: trade
  // cards, bookmarks, the activity log. The Time / P&L / Tier sort controls are
  // dropped (the seed), and so is the overlay's `Back to the battle` — there is
  // nothing to go back FROM once Tape is a section rather than a page.
  tapeTrades: 'Trades',
  tapeNoTrades: 'No trades yet',
  // THE BOOKMARK DOT'S NEW HOME (the founder's ruling on §4 #12). The header
  // link's dot said only "there is at least one"; as a section header it can
  // say how many, which is the same fact with the number restored. Nowhere on
  // the board.
  tapeBookmarks: (n) => (n > 0 ? `Bookmarks · ${n}` : 'Bookmarks'),
  tapeNoBookmarks: 'No bookmarks yet',
  // The shipped Game Tape's own fallback for a bookmarked entry that carries
  // no words (GameTapeView.jsx:413) — kept identical so one bookmark cannot
  // read two ways across the two surfaces.
  tapeBookmarkNoDetail: 'No details available',
  tapeUnbookmark: 'Remove this bookmark',
  tapeActivityLog: 'Activity log',
  tapeActivityShow: 'Show the activity log',
  tapeActivityHide: 'Hide the activity log',

  // ── The bagger moment (A3.6, D-97) ─────────────────────────────────────────
  // Two strings, both from PERSISTED scoring, both about one crossing.
  //
  // `{pts}` is THE BAGGER BADGE'S OWN BONUS — `THRESHOLD_POINTS.bagger`, +15,
  // the entry the scorer sums for this crossing and nothing else
  // (`calculatePointsServer`, api/_utils/agentScoring.js:95-97, called at :286;
  // the client twin is baggerBombUtils.js:285-289, called at :613). The caller
  // passes it: this module holds words, not scoring constants.
  //
  // IT USED TO BE THE CONVICTION TIER MULTIPLIER — `Bagger hit · {mult}× banked`,
  // ruling 8 as first ruled. The A3.6 review measured that the bonus is FLAT:
  // the tier multiplier scales `basePoints` only (agentScoring.js:267-270,
  // baggerBombUtils.js:584-587), never the badge bonus, so the line named a
  // number the scoring path never applied to the thing the line is about — one
  // row, two sources, the family BUILD_RULES §9 exists to close. Ruling 8 is
  // CORRECTED (Sep 7, 2026; PHASE_A3_RULINGS_AND_AMENDMENTS_V1.md §2 ruling 8):
  // the footer states the banked bonus, and states nothing the scorer did not
  // bank.
  //
  // `banked` stands unchanged: the persisted peak is monotonic within the day
  // (agent-evaluate.js:893-900, off the scorer's `effectiveMax`), so the badge —
  // and with it the flat bonus — cannot be taken back by a later fall.
  //
  // NO NUMBER, NO CLAIM ABOUT ONE. An unreadable amount leaves `Bagger hit`
  // alone rather than nothing at all: the crossing is persisted and true on its
  // own, and the amount is the only part that could be wrong. Whether the
  // crossing happened is still the caller's question, not this one's.
  //
  // `{pct}` is the bagger LINE, `+{baseATR}%` (ruling 9) — the persisted
  // threshold the row reads at that price, the same number deriveTierPrices
  // turns into `Bagger $`. Not the piece's current percent, which is a live
  // value and would disagree with the line the moment the price moved again.
  //
  // The PERCENT keeps its decimal — `+3.0%`, not `+3%` (review lens 5). Every
  // other percent in this view carries one (computeProximity.js:213-215), and
  // one number in a family reading differently is the seam a reader trips on.
  baggerFooter: (pts) => {
    if (typeof pts !== 'number' || !Number.isFinite(pts) || pts <= 0) return 'Bagger hit';
    return `Bagger hit · +${pts} banked`;
  },
  baggerBubble: (symbol, pct) => {
    if (typeof symbol !== 'string' || !symbol.trim()) return null;
    if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0) return null;
    return `Bagger · ${symbol.trim()} hit +${pct.toFixed(1)}%`;
  },
  // The bubble's own eyebrow, so it reads as a RECORD like the tape's cards do
  // rather than as speech (D-98: the kind carries its colour as text).
  baggerEyebrow: 'Bagger',

  // ── The layout (A4) ────────────────────────────────────────────────────────
  // Game Tape is ONE header link that opens the shipped view full-screen; the
  // way back names the page it returns to. No `···` menu (rulings §2.5).
  gameTape: 'Game Tape',
  gameTapeBack: 'Back to the battle',

  // The mobile chat sheet — a non-modal region with three detents. The cycle
  // control is named for what its NEXT activation does; the region carries
  // the name a screen reader lands on; `new activity` rides the control's
  // name while the unread dot shows (the dot itself is decorative).
  sheetName: 'Agent chat',
  sheetOpen: 'Open the chat',
  sheetGrow: 'Show more of the chat',
  sheetCollapse: 'Collapse the chat',
  // The desktop strip's control (A2.4). Named for what its next activation
  // does, exactly as the sheet's three are.
  sheetExpand: 'Expand the chat',
  sheetUnread: 'new activity',
  // The cycle control's whole name: the action, plus `new activity` while
  // the dot shows. Composed HERE so the rule lives with the words.
  sheetHandleName: (action, unread) => (unread ? `${action} · ${BATTLE_VIEW_COPY.sheetUnread}` : action),

  // Accessible names for the two Why? tap surfaces (A4.3, review F16): the
  // row button is named for its verb and its piece, the score header for the
  // book — a short name instead of the whole visible content of each.
  whyName: (symbol) => `Why? ${symbol}`,
  whyBookName: 'Why? · the whole book',

  // ── Filing a directive by chip (voice-layer grounding §6.2 / §6.3) ─────────
  // A directive chip says what it FILES; a filing's outcome says only what the
  // client can be held to. ONE source with the League arena (decisionRecord.js).
  filesChip: (text) => recordFilesChip(text),
  noChangeStatusLine: NO_CHANGE_STATUS_LINE,
  filingConflict: FILING_CONFLICT_LINE,
  filingBudget: FILING_BUDGET_LINE,
  filingRejected: FILING_REJECTED_LINE,
  filingFailed: FILING_FAILED_LINE,
  filingFailureLine: (status) => recordFilingFailureLine(status),

  receiptLine: (receipt) => {
    if (!receipt || typeof receipt !== 'object') return null;
    if (receipt.state === 'filed') return BATTLE_VIEW_COPY.filed(receipt.at);
    if (receipt.state === 'replaced') return BATTLE_VIEW_COPY.replaced(receipt.at);
    if (receipt.state === 'expired') return BATTLE_VIEW_COPY.expired;
    return null;
  },

  // ── The cockpit (Cockpit Build 2a, spec docs/COCKPIT_BUILD2A_SPEC_V1_0.md §8) ─
  // Every string the cockpit renders. Times arrive ALREADY FORMATTED by the
  // shared renderer (api/_utils/callRecords/copy.js, 12-hour clock — a check
  // named by its slot, D-83), so a check reads the same on a tile, in the
  // sheet and on its chat card. The record's own field for the watch list is
  // shown as "Monitoring" (ruling R2A-6); the honesty rules above bind here
  // too: nothing says the agent held, nothing claims comprehension.
  cockpitTab: 'Cockpit',
  // The Chat tab while another section shows and the chain counts unread
  // (spec §5; the `Bookmarks · n` precedent).
  // Capped at 9+ — the widest label the four-tab width budget was measured at
  // (CharacterPane PANE_HEADER_FIXED_COCKPIT_PX: "Chat · 9+" 338.4 px).
  paneSectionChatUnread: (n) => (Number.isFinite(n) && n > 0 ? `Chat · ${n > 9 ? '9+' : n}` : 'Chat'),
  cockpitSwitchName: 'Board or cockpit',
  cockpitSwitchBoard: 'Board',
  cockpitSwitchCockpit: (n) => (Number.isFinite(n) && n > 0 ? `Cockpit · ${n}` : 'Cockpit'),

  // Group headers (§7.1, §8.1).
  cockpitGroupNeedsYou: '⚡ Needs you',
  cockpitGroupWaiting: '⏱ Waiting on the check',
  cockpitGroupMonitoring: '👁 Monitoring',
  cockpitGroupEarlier: 'Earlier',
  cockpitShowAll: (n) => (Number.isFinite(n) ? `Show all · ${n}` : 'Show all'),
  cockpitMonitoringFrom: (check) => (check ? `From ${check}` : null),
  cockpitMonitoringChipName: (symbol) => (symbol ? `Research ${symbol}` : null),

  // The top row (§5): the prices' vintage, the next check, the budget.
  cockpitVintage: (check, next) => {
    if (!check) return null;
    const nextTime = slotLabel(next);
    return nextTime ? `Prices as of ${check} · next ~${nextTime}` : `Prices as of ${check}`;
  },
  cockpitMessagesLeft: (n) => (Number.isFinite(n) ? `${n} ${n === 1 ? 'message' : 'messages'} left` : null),
  // A failed read says so — never "No calls yet" for records it could not see (review L3-2).
  cockpitReadError: "Couldn't load your agent's calls right now.",
  cockpitEmpty: (next) => {
    const nextTime = slotLabel(next);
    return nextTime
      ? `No calls yet. Your agent declares calls at its checks — next one ~${nextTime}.`
      : 'No calls yet. Your agent declares calls at its checks.';
  },

  // Kind labels and the eyebrow (§7.3, §8.1).
  cockpitKindCalledShot: 'Called shot',
  cockpitKindConfirmation: 'Confirmation',
  cockpitKindUpside: 'Upside call',
  cockpitEyebrowFrom: (check) => (check ? `from ${check}` : null),
  cockpitRestated: (check) => (check ? `Restated at ${check}` : 'Restated'),
  // Cockpit Build 2b (Amendment D-12 rev 4; spec §5.2, from merge): one card per stock plan. A card holding
  // open calls at more than one level marks the moved line; earlier levels show on expand, newest first.
  // Nothing on a card ever calls it "one call".
  cockpitLatestLine: (check) => (check ? `Latest line · ${check}` : 'Latest line'),
  cockpitLineMoved: (from, to) => (from && to ? `Line moved ${from} → ${to}` : null),
  cockpitEarlierLevel: (side, price, check) => {
    if (!price || (side !== 'above' && side !== 'below')) return null;
    return check ? `${side} ${price} · from ${check}` : `${side} ${price}`;
  },
  // D-12 condition 1: a command shown beside a new ask names the line it was given on — never "covering" the new one.
  cockpitCommandOnLine: (price) => (price ? `Your command · on the ${price} line` : 'Your command'),
  // Review (Astra P2, 2026-10-21): when a card's buttons answer a line OTHER than its headline (an older new ask
  // beside your live command), that line's own row sits directly above them — the line, its level, its check.
  cockpitAnswerTarget: (sym, side, price, check) => {
    const line = sym && price && (side === 'above' || side === 'below') ? `${sym} ${side} ${price}` : (sym || null);
    return ['Needs your answer', line, check ? `from ${check}` : null].filter(Boolean).join(' · ');
  },
  // The sheet's earliest wording of a folded thread (review L6-3).
  cockpitCalledAt: (check) => (check ? `Called at ${check}` : 'Called'),

  // The buttons (§7.3; R2A-11: every override shows its cost).
  cockpitAnswerGoIfTriggers: 'Go if it triggers',
  cockpitAnswerConfirm: 'Confirm',
  cockpitAnswerHold: 'Hold',
  cockpitAnswerHoldOff: 'Hold off · 1 message',
  cockpitAnswerGoInstead: 'Go instead · 1 message',
  cockpitSending: 'Sending…',
  cockpitWaitingHeard: 'Waiting · your last answer hasn\'t been heard yet.',
  // HEAD's endpoint keeps a HEARD call directive pending while it stays live —
  // until it expires, is replaced, retired or killed (directiveUtils.js
  // isCallDirectivePendingAt; Amendment C rev 3): the line for that case — and
  // for any case the records cannot place — so the one above is never shown
  // without the record saying the answer is unheard. "Active", not "in force":
  // the agent is coached, not compelled (review L6-8).
  cockpitWaitingActive: (time) => (time
    ? `Waiting · your last answer stays active until ${time}.`
    : 'Waiting · your last answer is still active.'),
  cockpitOneAtATime: 'One call at a time.',
  // A folded thread's directive answer, live or not (Amendment C-6 rev 3), naming the wording it was given on.
  // Cockpit Build 2b (D-4): a chosen name rides the words — "bring in DHR" for a replace, "go instead, bring in DHR"
  // for a named go_now — read from the directive's own record by the caller (never from playerResponse).
  // Cockpit Build 2b (flag on, review CB6-4): `go_now` is quoted in the words the player tapped there —
  // "exit instead" on an exit, "bring it in" on an entry (§6.2; V5-2 retires "Go instead").
  cockpitThreadAnswer: (answer, filedTime, wordingCheck, pickSymbol = null, { v2 = false, direction = null } = {}) => {
    const named = typeof pickSymbol === 'string' && pickSymbol ? pickSymbol : null;
    const goNow = v2 ? (direction === 'exit' ? 'exit instead' : 'bring it in') : 'go instead';
    const word = answer === 'hold' ? 'hold off'
      : answer === 'go_now' ? (named ? `${goNow}, bring in ${named}` : goNow)
        : answer === 'replace' && named ? `bring in ${named}` : null;
    if (!word) return null;
    const head = filedTime ? `You said ${word} · ${filedTime}` : `You said ${word}`;
    return wordingCheck ? `${head}, on the ${wordingCheck} wording` : head;
  },
  // An agreement given on an EARLIER wording of a folded thread (review L6-2).
  cockpitThreadAgreed: (filedTime, wordingCheck) => {
    const head = filedTime ? `You agreed · ${filedTime}` : 'You agreed';
    return wordingCheck ? `${head}, on the ${wordingCheck} wording` : head;
  },

  // The sheet (§7.5).
  cockpitSheetName: 'Call details',
  cockpitSheetClose: 'Close',
  cockpitSheetLevel: 'Level',
  cockpitSheetDeadline: 'Deadline',
  cockpitSheetPrice: 'Price',
  cockpitSheetLevelValue: (side, price) => (price && (side === 'above' || side === 'below') ? `${side} ${price}` : null),
  cockpitSheetPriceValue: (price, check) => (price ? (check ? `${price} · as of ${check}` : price) : null),
  cockpitSheetDefault: (intent) => (intent ? `If you say nothing · ${intent}` : null),
  // The agent's default as an INTENT, never a promise (Build 1a §4): "bring in
  // AMD for KO", "exit MU for AMAT", "hold". An upside call has none (C-2).
  // The one intent renderer: copy.js's symbol-less renderIntentLine had no
  // consumer and is retired (founder ruling Oct 4; review L6-10).
  cockpitIntent: (defaultAction, direction, symbol, counterpart) => {
    if (defaultAction === 'hold') return 'intent: hold';
    if (defaultAction !== 'act' || !symbol) return null;
    const verb = direction === 'exit' ? 'exit' : direction === 'entry' ? 'bring in' : null;
    if (!verb) return null;
    return `intent: ${verb} ${symbol}${counterpart ? ` for ${counterpart}` : ''}`;
  },
  cockpitSheetReceipts: 'Receipts',
  cockpitSheetFromCheck: (check) => (check ? `From ${check} →` : null),
  cockpitSheetCheckGone: 'That check is no longer in the chat',
  cockpitReceiptFiled: (time) => (time ? `Filed ${time}` : 'Filed'),
  // "Instruction", not "answer": a chat or chip filing replaces a cockpit
  // answer too, and the `superseded` event does not say which wrote it
  // (founder ruling Oct 4; review L6-11 a).
  cockpitReceiptReplaced: (time) => (time ? `Replaced by a later instruction · ${time}` : 'Replaced by a later instruction'),
  // Cockpit Build 2b (spec §7.1, from merge): the receipt names the replacing filing's source, as the tag does.
  cockpitReceiptReplacedBy: (by, time) => {
    const head = by === 'cockpit' ? 'Replaced by your later command' : (by === 'chat' ? 'Replaced by a chat instruction' : 'Replaced by a later instruction');
    return time ? `${head} · ${time}` : head;
  },
  cockpitObservedHit: (price, check) => (price ? (check ? `Hit at ${price} · ${check}` : `Hit at ${price}`) : null),
  cockpitObservedExpired: (price, check) => (price ? (check ? `Expired at ${price} · ${check}` : `Expired at ${price}`) : null),

  // From the cockpit (§8.2): the label on a chat directive card whose exchange
  // the answer endpoint wrote (`source: 'cockpit'`).
  fromCockpit: 'From the cockpit',

  // ── Cockpit Build 2b — the V5 screen, flag on (spec docs/COCKPIT_BUILD2B_SPEC_V1_0.md §5–§7) ──
  // `agent` is the battle's own agent name; times arrive formatted ("the 1:30 PM check", "1:32 PM").
  // No line here is ever attributed to the agent (V5-7): every sentence renders a record field.
  // §5.1 the commands meter.
  cockpit2bMeterLeft: (n) => (Number.isFinite(n) && n > 0 ? `· ${n} left` : 'No commands left'),
  cockpit2bMeterAria: (n, limit) => (Number.isFinite(n) ? `${n} of ${limit} commands left` : null),
  // §5.2 the Now card.
  cockpit2bNowHeader: '⚡ Now',
  cockpit2bQuiet: (agent, next) => {
    const t = slotLabel(next);
    return t ? `Nothing needs you · ${agent}'s next check ~${t}` : 'Nothing needs you';
  },
  cockpit2bKindExit: 'Exit call',
  cockpit2bKindHold: 'Hold call',
  cockpit2bKindEntry: 'Entry call',
  cockpit2bKindUpside: 'Upside call',
  cockpit2bKindCommand: (wordingCheck) => (wordingCheck ? `Your command · on the ${wordingCheck} wording` : 'Your command'),
  cockpit2bFrom: (check) => (check ? `from ${check}` : null),
  cockpit2bPlanExitAct: (agent, sym, side, price, deadline) => `${agent} plans to exit ${sym} if it goes ${side} ${price}${deadline ? ` ${deadline}` : ''}.`,
  cockpit2bPlanExitHold: (agent, sym, side, price, deadline) => `${agent} calls ${sym} ${side} ${price}${deadline ? ` ${deadline}` : ''}, and plans to hold it.`,
  cockpit2bPlanEntryAct: (agent, sym, cp, side, price, deadline) => `${agent} plans to bring in ${sym}${cp ? ` for ${cp}` : ''} if it goes ${side} ${price}${deadline ? ` ${deadline}` : ''}.`,
  cockpit2bPlanEntryHold: (agent, sym, side, price, deadline) => `${agent} calls ${sym} ${side} ${price}${deadline ? ` ${deadline}` : ''}, and plans to wait.`,
  cockpit2bSilence: (verb) => (verb ? `If you say nothing, it ${verb}.` : null),
  // "as of" only beside the price it dates; without the check's cited price the line is the deadline alone.
  cockpit2bFacts: (deadline, sym, price, check) => [deadline, price ? `${sym} ${price}` : null, price && check ? `as of ${check}` : null].filter(Boolean).join(' · ') || null,
  // §6.2 the buttons: agreeing first and free; every override carries one bullet.
  cockpit2bConfirmExit: 'Confirm exit',
  cockpit2bGoIfTriggers: 'Go if it triggers',
  cockpit2bHold: 'Hold',
  cockpit2bHoldOff: 'Hold off',
  cockpit2bExitInstead: 'Exit instead',
  cockpit2bBringItIn: 'Bring it in',
  cockpit2bNoCommands: 'No commands left',
  cockpit2bOneCommandAria: (label) => (label ? `${label} · 1 command` : null),
  // A LEAGUE battle's overrides charge the group's daily chat budget (D-1: the league branch is unchanged).
  cockpit2bOneMessageAria: (label) => (label ? `${label} · 1 message` : null),
  cockpit2bSending: 'Sending…',
  cockpit2bDone: 'Done',
  // The command card (§5.2 "After an answer"): the canonical text verbatim, then the receipts.
  cockpit2bFiled: (time) => (time ? `Filed ${time}` : 'Filed'),
  cockpit2bNotYetHeard: 'not yet heard',
  // Left the slot unheard (killed, expired, replaced, retired): no hearing is promised that cannot come (2a ruling Oct 4).
  cockpit2bNotHeard: 'not heard',
  cockpit2bNoCheckLeft: 'no check left today',
  // §5.3 Who comes in?
  cockpit2bWho: (sym) => (sym ? `Who comes in for ${sym}?` : null),
  cockpit2bBrowse: 'Browse the bench →',
  // The agent's own names (R2B-5: either of its lists — the newest check's anticipation candidates or its
  // kept watch list). The spec words the label "{Agent} is eyeing"; the C1 honesty guard
  // (src/components/Dashboard/desk/deskHonesty.test.js) bans "eyeing" by name as an agent verb, and spec §11
  // forbids loosening a guard — so the label names the RECORD, the lists, as its sibling names the bench.
  // A founder question in the build report (keep this, or amend the guard).
  cockpit2bAgentLists: (agent) => `From ${agent}'s lists`,
  cockpit2bAgentListMark: (agent) => `On ${agent}'s list`,
  cockpit2bByRank: 'From your bench · by rank',
  cockpit2bChipLocked: 'locked',
  cockpit2bChipHeld: 'in your book',
  cockpit2bChipCant: 'can\'t come in',
  cockpit2bComesIn: (inn, sym) => (inn && sym ? `✓ ${inn} comes in if ${sym} exits · filed` : null),
  cockpit2bLetChoose: (agent) => `Let ${agent} choose`,
  cockpit2bCancel: 'Cancel',
  // §5.4 Also live.
  cockpit2bAlsoLive: (n) => (Number.isFinite(n) ? `Also live · ${n}` : 'Also live'),
  cockpit2bWordExit: 'exit',
  cockpit2bWordHold: 'hold',
  cockpit2bWordEnter: 'enter',
  cockpit2bWordWait: 'wait',
  cockpit2bWordCommand: 'your command',
  cockpit2bWordFollowing: 'following',
  cockpit2bWordUpside: 'upside',
  cockpit2bWordAgreed: 'you agreed',
  // An answered call whose command no longer stands (review CB3-4): the fact its card's tag already names.
  cockpit2bWordRetired: 'command retired',
  cockpit2bWordReplaced: 'replaced',
  cockpit2bWordAnswered: 'you answered',
  // Amendment E rev 2 (E-2): an exit whose stock is in no tier of the book (the tag reads "Not in your book").
  cockpit2bWordNotHeld: 'not in your book',
  // Amendment F (F-2): an entry whose stock IS in the book (the tag reads "Already in your book").
  cockpit2bWordAlreadyHeld: 'already in your book',
  cockpit2bLineMovedShort: 'line moved',
  cockpit2bMore: (n) => `+${n} more`,
  cockpit2bFewer: 'fewer',
  cockpit2bSpotlight: 'Spotlight →',
  cockpit2bChipAria: (text) => (text ? `${text} — open` : null),
  // The expanded chip's header collapses it (review CB5-10: its name says so).
  cockpit2bChipAriaCollapse: (text) => (text ? `${text} — close` : null),
  // §5.5 the day line (counts records; never tappable).
  cockpit2bDayLine: (calls, hit, expired, answered) => `Today · ${calls} ${calls === 1 ? 'call' : 'calls'} · ${hit} hit · ${expired} expired · you answered ${answered}`,
  // §5.6 Earlier, collapsed.
  cockpit2bEarlierRow: (sym, arrow, level, tag) => [[sym, arrow, level].filter(Boolean).join(' '), tag].filter(Boolean).join(' · '),
  cockpit2bEarlierToggle: (open) => (open ? 'Hide earlier calls' : 'Show earlier calls'),
  // §6.3 the replace confirmation.
  // A 409 naming a command whose call this screen has not loaded: the command, without a symbol it cannot read.
  cockpit2bReplaceCommand: (sym, text, agent) => `This replaces your command${sym ? ` on ${sym}` : ''} — "${text}". ${agent} stops hearing it at its next check.`,
  cockpit2bReplaceChat: (text, agent) => `This replaces your chat instruction — "${text}". ${agent} stops hearing it at its next check.`,
  cockpit2bReplace: 'Replace',
  cockpit2bKeep: 'Keep current command',
  // §5.7 the bench sheet (6A).
  cockpit2bBenchTitle: 'The bench',
  cockpit2bBenchSub: (n, available, asOf) => [`${n} ${n === 1 ? 'name' : 'names'}`, `${available} available`, asOf ? `prices as of ${asOf}` : null].filter(Boolean).join(' · '),
  cockpit2bSortRank: 'Rank',
  cockpit2bSortMove: 'Today\'s move',
  cockpit2bSortSector: 'Sector',
  cockpit2bSortName: 'Sort the bench',
  cockpit2bPick: 'Pick',
  cockpit2bLockedUntil: (time) => (time ? `locked until ${time}` : 'locked'),
  cockpit2bNotSwappable: 'can\'t come in',
  cockpit2bNoPrice: 'no price',
  cockpit2bAvailable: 'available',
  cockpit2bFiledBar: (text) => (text ? `✓ Filed · ${text}` : '✓ Filed'),
  cockpit2bBenchLoading: 'Reading the bench…',
  cockpit2bBenchError: 'Couldn\'t read the bench right now.',
  cockpit2bBenchClose: 'Close',
  cockpit2bAtr: (pct) => (pct ? `ATR ${pct}` : null),
  cockpit2bResearchSymbol: (sym) => (sym ? `Research ${sym}` : null),
  // §5.9 the Research pill and the composer line.
  cockpit2bResearch: 'Research',
  cockpit2bResearchAria: 'Research · open the bench',
  cockpit2bComposerLine: (sym) => (sym ? `Sending a directive replaces your cockpit command on ${sym}.` : null),
  // §5.8 board-row dots.
  cockpit2bDotCommand: 'your live command',
  cockpit2bDotOpen: 'an open call',
});

/**
 * THE COCKPIT'S FACT TABLE — Cockpit Build 2a (spec §7.4). ONE table, every
 * record fact the call-records writers can emit at HEAD, each mapped to the
 * tag it shows or, deliberately, to `null` (the fact shows no tag of its own).
 * A fact with no row here renders NO tag — never a guessed one —
 * and cockpitModel.test.js asserts the table covers every kind the writers
 * emit (the call states of candidate / flip / sweep, every CALL_EVENT_KINDS
 * entry of events.js, and both playerResponse kinds of the answer endpoint —
 * a filed directive in two rows, by whether the battle's slot still holds it).
 *
 * Tones (§9, ruling R2A-5): `yours` / `yoursOutline` — the player's answer
 * (teal; an agreement outlined) · `acted` (emerald) · `dropped` (amber) ·
 * `muted` (expired / ended / no matching trade / replaced) · `neutral` (live).
 * Gold is reserved; nothing here uses it.
 *
 * Every row renders from the record alone: `check` is a check label from the
 * shared renderer ("the 1:30 PM check"), `time` an exact filing minute,
 * `deadline` the call's own deadline phrase. A row whose time is missing says
 * the bare fact — never a guessed time.
 */
export const COCKPIT_FACT_TAGS = Object.freeze({
  // The call's state (candidate.js mints open | invalidated; flip.js hit |
  // expired_unresolved; sweep.js expired_unresolved | ended_with_battle).
  'state:open': Object.freeze({ tone: 'neutral', text: () => 'Live' }),
  'state:hit': Object.freeze({ tone: 'neutral', text: ({ check } = {}) => (check ? `Hit at ${check}` : 'Hit') }),
  // The bare fact: the call record carries no reason (no_observation /
  // level_non_finite / level_implausible live only in the declarations
  // record's minted[]), so the spec's "· price line out of range" would be a
  // guess for two of the three (review L6-4 — a founder ruling, recorded).
  // Cockpit Build 2b (Amendment D-9; spec §7.2) supersedes the note above for
  // new records: the call now carries the mint's own reason
  // (`invalidatedReason`), read through COCKPIT_DROPPED_REASON_TEXT below; a
  // record without one (minted before 2b) or an unknown reason keeps the bare fact.
  'state:invalidated': Object.freeze({ tone: 'dropped', text: ({ reason } = {}) => (reason ? `Dropped · ${reason}` : 'Dropped') }),
  'state:expired_unresolved': Object.freeze({ tone: 'muted', text: ({ deadline } = {}) => (deadline ? `Expired · ${deadline}` : 'Expired') }),
  'state:ended_with_battle': Object.freeze({ tone: 'muted', text: () => 'Battle ended' }),
  // The answer on the call (call-response.js playerResponse.kind).
  'answer:ack': Object.freeze({ tone: 'yoursOutline', text: ({ time } = {}) => (time ? `You agreed · ${time}` : 'You agreed') }),
  // A filed directive no check has heard, by where it stands (founder ruling
  // Oct 4): "not yet heard" ONLY while it is still the battle's current one
  // (cockpitModel callTag — the slot holds its thread, live). Killed, expired
  // or suppressed before any check heard it — or replaced while its
  // `superseded` event is not loaded (once it is, the tile reads "Replaced by
  // a later instruction") — it reads "not heard": no hearing is promised that
  // cannot come (review L6-11 c). Teal, as every state of the player's answer
  // is (ruling R2A-5: filed, heard, agreed).
  'answer:directive': Object.freeze({ tone: 'yours', text: () => 'Filed · not yet heard' }),
  'answer:directive_left_slot': Object.freeze({ tone: 'yours', text: () => 'Filed · not heard' }),
  // Cockpit Build 2b (spec §7.1, from merge): a filing still the battle's
  // current one, when no check is left to hear it today — the battle's next
  // decision time is absent or after the close of the session the filing
  // falls in (horizon.js sessionCloseAfter). Never "not yet heard" then.
  'answer:directive_no_check': Object.freeze({ tone: 'yours', text: () => 'Filed · no check left today' }),
  // Cockpit Build 2b (Amendment D-10, spec §7.1, from merge): a later call the
  // agent minted under your LIVE command, on its plan, within 1 % of the
  // answered line, at a check whose `declared` event names the command's
  // thread (prompt inclusion — never causation). Never on the call you answered.
  'call:following': Object.freeze({ tone: 'yoursOutline', text: () => 'Following your call' }),
  // Cockpit exit-not-held (Amendment E rev 2, E-2): an OPEN exit call whose stock is in no tier of the battle
  // document the screen reads, with no answer and no act on it. No answer is legal then (the endpoint refuses
  // them all), so the tile offers none and says why, in the same words as D-9's "Dropped · not in your book".
  'call:not_held': Object.freeze({ tone: 'muted', text: () => 'Not in your book' }),
  // Cockpit entry-already-held (Amendment F, F-2): the entry-side mirror — an OPEN entry call whose stock IS in some
  // tier of the battle document the screen reads, with no answer and no act on it, and not an upside call (which
  // keeps its own treatment). No answer is legal then (the endpoint refuses them all), so the tile offers none.
  'call:already_held': Object.freeze({ tone: 'muted', text: () => 'Already in your book' }),
  // The call events (events.js CALL_EVENT_KINDS).
  'event:declared': null, // the mint — the eyebrow's "from the {t} check" already says it
  'event:answered': null, // the answer's own receipt — the tag reads the call's playerResponse
  'event:heard': Object.freeze({ tone: 'yours', text: ({ check } = {}) => (check ? `Heard at ${check}` : 'Heard') }),
  'event:acted': Object.freeze({ tone: 'acted', text: ({ check } = {}) => (check ? `Acted at ${check}` : 'Acted') }),
  'event:no_matching_trade': Object.freeze({ tone: 'muted', text: ({ check } = {}) => (check ? `No matching trade at ${check}` : 'No matching trade') }),
  'event:expired': Object.freeze({ tone: 'muted', text: ({ deadline } = {}) => (deadline ? `Expired · ${deadline}` : 'Expired') }),
  'event:ended_with_battle': Object.freeze({ tone: 'muted', text: () => 'Battle ended' }),
  // Any later filing — chat, chip or cockpit — writes this event (directiveWriter.js). Cockpit Build 2b
  // (spec §7.1, from merge): the tag names the replacing filing's SOURCE, read from the battle's own
  // exchange for the event's `supersededBy` thread — `source: 'cockpit'` → your later command; any other
  // source → a chat instruction. With that exchange not found, the 2a fact: a later instruction.
  'event:superseded': Object.freeze({
    tone: 'muted',
    text: ({ by, time } = {}) => {
      const head = by === 'cockpit' ? 'Replaced by your later command' : (by === 'chat' ? 'Replaced by a chat instruction' : 'Replaced by a later instruction');
      return time ? `${head} · ${time}` : head;
    },
  }),
  // Cockpit Build 2b (Amendment D-6): the command retired because its stock left the book (or, for a
  // chosen name, the name came into the book another way), at the check whose step retired it.
  'event:retired': Object.freeze({
    tone: 'muted',
    text: ({ symbol, came = false, check } = {}) => {
      if (!symbol) return 'Command retired';
      const what = came ? `${symbol} came in another way` : `${symbol} left the book`;
      return check ? `Command retired · ${what} at ${check}` : `Command retired · ${what}`;
    },
  }),
});

/**
 * Cockpit Build 2b (Amendment D-9; spec §7.2): the mint's invalidation
 * reasons (api/_utils/callRecords/validate.js invalidationReason — the three
 * it returns at HEAD — and, from Amendment E rev 2, candidate.js
 * EXIT_NOT_HELD_REASON), one short line each. Any other reason → the bare
 * "Dropped" (no row here, no guessed line).
 */
export const COCKPIT_DROPPED_REASON_TEXT = Object.freeze({
  level_implausible: 'price line out of range',
  level_non_finite: 'no usable price line',
  no_observation: 'no price at that check',
  exit_not_held: 'not in your book',
});

/**
 * One plain line per refusal (spec §8.3, ruling R2A-8), chosen from the
 * response BODY — never the chip's status-only mapper (decisionRecord.js
 * filingFailureLine). Every line but the last says nothing was filed, because
 * the server proved it; the last never does, because the server may have
 * committed. `null` status = no response at all.
 */
export function cockpitRefusalLine(status, body = null, { pendingLine = null, v2 = false, names = {} } = {}) {
  const reason = body && typeof body === 'object' ? body.reason : null;
  const error = body && typeof body === 'object' ? body.error : null;
  // Cockpit Build 2b (spec §7.3, flag on): the changed rows. replace_unconfirmed opens the confirmation and
  // shows no line; the budget is commands; a name that can't come in, or a stock already gone, says which.
  if (v2) {
    if (status === 409 && reason === 'replace_unconfirmed') return null;
    // A league battle's budget is still the group's daily chat messages (D-1's league branch).
    if (status === 409 && reason === 'budget') return names.league ? 'No messages left — nothing was filed.' : 'No commands left — nothing was filed.';
    if (status === 400 && error === 'ineligible_action') {
      if (typeof reason === 'string' && reason.startsWith('pick_') && names.pickSymbol) return `${names.pickSymbol} can't come in right now — nothing was filed.`;
      if (reason === 'counterpart_unavailable' && names.counterpart) return `${names.counterpart} can't come in right now — nothing was filed.`;
      if (reason === 'symbol_not_held' && names.symbol) return `${names.symbol} has already left your book — nothing was filed.`;
      // Amendment F (F-2): an entry whose stock is already in the book; and the entry's named counterpart that has left it.
      if (reason === 'symbol_already_held' && names.symbol) return `${names.symbol} is already in your book — nothing was filed.`;
      if (reason === 'counterpart_not_held' && names.counterpart) return `${names.counterpart} has already left your book — nothing was filed.`;
    }
  }
  if (status === 409) {
    switch (reason) {
      case 'already_answered': return 'You\'ve already answered this call.';
      case 'expired': return 'This call\'s deadline has passed — nothing was filed.';
      case 'parent_not_active': return 'This battle isn\'t active — nothing was filed.';
      case 'belief_mismatch': return 'Your agent\'s instructions changed a moment ago — nothing was filed. Check the tile and try again.';
      // The spec's line by default; the tile's own slot line when it knows
      // the answer in force was already heard (cockpitModel overrideBlockOf).
      case 'directive_pending': return `${typeof pendingLine === 'string' && pendingLine ? pendingLine : BATTLE_VIEW_COPY.cockpitWaitingHeard} ${BATTLE_VIEW_COPY.cockpitOneAtATime}`;
      case 'budget': return 'No messages left — nothing was filed.';
      default: return 'Couldn\'t confirm your answer. Check the tile before trying again.';
    }
  }
  if (status === 429) return 'Too many taps — try again in a minute.';
  if (status === 404 && error === 'cockpit_unavailable') return 'The cockpit is off for this battle — nothing was filed.';
  if (status === 400) return 'That answer isn\'t available for this call — nothing was filed.';
  if (status === 403) return 'Only the battle\'s owner can answer its calls.';
  if (status === 401) return 'Sign in again to answer.';
  return 'Couldn\'t confirm your answer. Check the tile before trying again.';
}

export default BATTLE_VIEW_COPY;
